/* eslint-disable no-confusing-arrow */
/* eslint-disable @typescript-eslint/indent */
import React, { useEffect, useState } from 'react';
import HotelApi, { HotelApiProduct } from '@customTypes/hotel-api';
import AVAILABILITY, { PRODUCT_CATEGORY_TEXT } from '@constants/product';
import Currency from '@components/common/Currrency/Currency';
import showClosedForSeasonBanner from '@helpers/showClosedForSeasonBanner';
import useHotSpot from '@hooks/useHotSpot';
import { HotSpotMin } from '@customTypes/hot-spot';
import { FAIR_PRICING_SERVICE_FEE_RATE } from '@constants/fairPricing';
import ChevronIcon from '@solar/assets/images/directional/chevron';
import { useSearchContext } from '@context/SearchContext2025';
import { getDateFromString } from '@utils/Search/search';
import { ShouldShowFairPricingFromHotelAPI } from '@utils/fairPricing';
import { useExperiment } from '@context/ExperimentContext';
import Button from '@solar/common/Button/ButtonV2';
import { AMPLITUDE_EXPERIMENT_VARIANT_NAME } from '@constants/amplitudeExperiments';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import BadgeSRP from '../Badge/BadgeSRP';

type Props = {
  hotel: HotelApi;
  href?: string;
  onViewAll?: (hotel: HotelApi) => void;
  finalProducts: HotelApiProduct[];
  sortedProducts: HotelApiProduct[];
  filteredProducts: HotelApiProduct[];
  searchedDate: string;
};

export default function Products({
  hotel,
  href = '',
  onViewAll = () => null,
  finalProducts,
  sortedProducts,
  filteredProducts,
  searchedDate,
}: Props) {
  const {
    searchParams: { product_category: productCategory },
  } = useSearchContext();
  const { ctasUpdateExperiment } = useFeatureFlagContext();

  const [noJS, setNoJS] = useState(true);

  const productCategoryText =
    productCategory && filteredProducts.length > 0
      ? PRODUCT_CATEGORY_TEXT[productCategory as keyof typeof PRODUCT_CATEGORY_TEXT] || ''
      : '';

  const searchDate =
    searchedDate && searchedDate !== 'any' ? getDateFromString(searchedDate) : undefined;

  const remainingProductsCount = finalProducts.length - sortedProducts.length;

  const isHotelUnavailableRelativeToToday = () => {
    if (!searchDate) {
      return !hotel.availability;
    }

    return false;
  };

  const { applyHotSpotDiscountToTotal, isDateWithinVisitWindow } = useHotSpot();

  const isPricingExperimentEnabled = false;
  const showFairPricing = ShouldShowFairPricingFromHotelAPI(hotel);

  const { hotelOffersExperiment } = useExperiment();
  const showHotelOffers = hotelOffersExperiment === 'on';
  useEffect(() => {
    setNoJS(false);
  }, []);

  return (
    <div className="flex flex-col w-full gap-2">
      <ul className="flex flex-col w-full gap-4">
        {sortedProducts &&
          sortedProducts.map((product: HotelApiProduct, index: number) => {
            const isProductUnavailable =
              isHotelUnavailableRelativeToToday() ||
              product.availability === AVAILABILITY.UNAVAILABLE ||
              (hotel.closedForSeason && showClosedForSeasonBanner(hotel.reopenDate, searchDate));

            const isHotSpot =
              searchDate &&
              product.is_hot_spot &&
              hotel.hotSpot &&
              isDateWithinVisitWindow(searchDate, hotel);

            const showPricingBadge =
              product.is_strikethrough_pricing &&
              product.max_price &&
              product.max_price > product.price &&
              product.discount_percentage &&
              !isHotSpot &&
              isPricingExperimentEnabled;

            const hasProductOffer = !!product.product_offer && showHotelOffers;

            return (
              <div className="flex flex-row w-full" key={product.id}>
                <li key={product.id} className="flex flex-row w-full">
                  <div className="flex flex-col w-full gap-4">
                    <div className="flex flex-row w-full justify-between  items-center">
                      <div>
                        <p
                          className={`text-sm font-rp-basetica  ${
                            isProductUnavailable ? 'text-rp-text-secondary' : 'text-rp-neutral'
                          }`}
                        >
                          {product.name}
                        </p>
                      </div>
                      <div>
                        {!searchDate && (
                          <p
                            className={`text-xs leading-14px font-rp-basetica text-rp-text-secondary text-right desktop:block ${
                              isProductUnavailable
                                ? 'text-rp-disabled opacity-50'
                                : 'text-custom-gray-2'
                            }`}
                          >
                            Starting at
                          </p>
                        )}
                        <div className="flex flex-row gap-1 justify-end">
                          <div
                            className={`text-22 font-rp-pn-semi-bold whitespace-nowrap ${
                              isProductUnavailable ? 'text-rp-disabled' : 'text-custom-black'
                            }`}
                          >
                            {/* TODO: Refactor to use a single <Currency> component */}
                            {!showFairPricing ? (
                              <div className="flex items-center">
                                <div className="flex items-center gap-1">
                                  <BadgeSRP
                                    product={product}
                                    hotel={hotel}
                                    date={searchDate}
                                    isProductUnavailable={isProductUnavailable}
                                    isPricingExperimentEnabled={isPricingExperimentEnabled}
                                    classes="max-h-17px"
                                    hasProductOffer={hasProductOffer}
                                  />
                                  <Currency
                                    value={
                                      product.is_hot_spot &&
                                      hotel.hotSpot &&
                                      hotel.hotSpot.is_current_hot_spot_hotel
                                        ? applyHotSpotDiscountToTotal(
                                            +product.price,
                                            hotel.hotSpot as HotSpotMin,
                                            showFairPricing,
                                          )
                                        : product.price
                                    }
                                    valueClasses={`text-base leading-5 font-rp-basetica text-rp-neutral ${
                                      isProductUnavailable ? 'opacity-50' : ''
                                    }`}
                                    isoCodeClasses="text-base leading-5 font-rp-basetica text-rp-neutral"
                                  />
                                </div>

                                {(product.is_hot_spot &&
                                  hotel.hotSpot &&
                                  hotel.hotSpot.is_current_hot_spot_hotel) ||
                                (showPricingBadge && !isProductUnavailable) ? (
                                  <Currency
                                    value={showPricingBadge ? product.max_price! : product.price}
                                    valueClasses="text-rp-text-secondary font-rp-basetica line-through leading-14px ml-2px text-xs"
                                    isoCodeClasses="hidden"
                                  />
                                ) : null}
                              </div>
                            ) : null}

                            {showFairPricing ? (
                              <div className="flex flex-col">
                                <div className="flex items-center justify-end">
                                  <div className="flex items-center gap-1">
                                    <BadgeSRP
                                      product={product}
                                      hotel={hotel}
                                      date={searchDate}
                                      isProductUnavailable={isProductUnavailable}
                                      isPricingExperimentEnabled={isPricingExperimentEnabled}
                                      classes="max-h-17px"
                                      hasProductOffer={hasProductOffer}
                                    />
                                    <Currency
                                      value={
                                        product.is_hot_spot &&
                                        hotel.hotSpot &&
                                        hotel.hotSpot.is_current_hot_spot_hotel
                                          ? applyHotSpotDiscountToTotal(
                                              +product.price,
                                              hotel.hotSpot as HotSpotMin,
                                              showFairPricing,
                                            )
                                          : product.price * (1 + FAIR_PRICING_SERVICE_FEE_RATE)
                                      }
                                      valueClasses={`text-base leading-5 font-rp-basetica text-rp-neutral ${
                                        isProductUnavailable ? 'opacity-50' : ''
                                      }`}
                                      isoCodeClasses="text-base leading-5 font-rp-basetica text-rp-neutral"
                                    />
                                  </div>

                                  {(product.is_hot_spot &&
                                    hotel.hotSpot &&
                                    hotel.hotSpot.is_current_hot_spot_hotel) ||
                                  (showPricingBadge && !isProductUnavailable) ? (
                                    <Currency
                                      value={
                                        showPricingBadge
                                          ? product.max_price! * (1 + FAIR_PRICING_SERVICE_FEE_RATE)
                                          : product.price * (1 + FAIR_PRICING_SERVICE_FEE_RATE)
                                      }
                                      valueClasses="text-rp-text-secondary font-rp-basetica line-through leading-14px ml-2px text-xs"
                                      isoCodeClasses="hidden"
                                    />
                                  ) : null}
                                </div>

                                <p
                                  className={`text-xs leading-14px font-rp-basetica text-rp-text-secondary text-right desktop:block ${
                                    isProductUnavailable ? 'opacity-50' : ''
                                  }`}
                                >
                                  Includes fees
                                </p>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`${
                        sortedProducts.length - 1 === index ? 'h-0' : 'h-1px'
                      } bg-rp-button-disabled`}
                    />
                  </div>
                </li>
              </div>
            );
          })}
      </ul>
      <div
        className={`flex flex-row items-center ${
          remainingProductsCount ? 'justify-between' : 'justify-end'
        }`}
      >
        {remainingProductsCount > 0 ? (
          <p className="text-xs leading-14px font-rp-basetica text-rp-neutral">
            {`+${remainingProductsCount} more ${productCategoryText} experience${
              remainingProductsCount > 1 ? 's' : ''
            }`}
          </p>
        ) : null}

        <div className="w-107px">
          {noJS ? (
            <a
              href={href}
              className="bg-solar-button-primary-default text-solar-inverted-primary rounded-solar-sm font-rp-basetica flex items-center justify-center h-11 text-base w-full"
            >
              <div className="flex gap-2 items-center">
                View All
                <ChevronIcon direction="right" color="#FEFEFE" />
              </div>
            </a>
          ) : (
            <Button
              variant={
                ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME
                  ? 'primary'
                  : 'ultramarine'
              }
              size="md"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onViewAll(hotel);
              }}
            >
              <div className="flex gap-2 items-center">
                View All
                <ChevronIcon direction="right" color="#FEFEFE" />
              </div>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
